import * as React from 'react';
import { Route, RouteProps, useRouteMatch } from 'react-router-dom';
import { classes } from '../../lib';

import Styles from './ScrollRoute.module.less';

type ScrollRouteProps = Omit<RouteProps, 'children' | 'path'> & {
    blink?: boolean;
    children?: React.ReactNode;
    path: NonNullable<RouteProps['path']>;
    className?: string;
    style?: React.CSSProperties;
};

const ScrollElement = ({
    children,
    blink = false,
    className,
    ...props
}: Pick<
    ScrollRouteProps,
    'children' | 'className' | 'style' | 'blink'
>): JSX.Element => {
    const match = useRouteMatch();
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [blinking, setBlinking] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (match === null) return;
        if (!scrollRef.current) return;

        scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        if (!blink) return;

        setBlinking(true);
        const blinkingTimeout = setTimeout(() => setBlinking(false), 3000);

        return () => clearTimeout(blinkingTimeout);
    }, [match]);

    return (
        <div
            onClick={() => setBlinking(false)}
            className={classes(className, blinking && Styles.focus)}
            {...props}
            ref={scrollRef}>
            {children}
        </div>
    );
};

const ScrollRoute = ({
    path,
    children,
    className,
    style,
    blink,
    ...props
}: ScrollRouteProps): JSX.Element => (
    <Route {...props} path={path}>
        {() => (
            <ScrollElement blink={blink} style={style} className={className}>
                {children}
            </ScrollElement>
        )}
    </Route>
);

export type { ScrollRouteProps };
export { ScrollRoute };
